import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { PartnerListingEditor, type PartnerEditorTab } from "@/components/partner/PartnerListingEditor";
import { PartnerMenusView } from "@/components/partner/PartnerMenusView";
import { PartnerHoursView } from "@/components/partner/PartnerHoursView";
import { PartnerHelpView } from "@/components/partner/PartnerHelpView";
import { PartnerPlanView } from "@/components/partner/PartnerPlanView";
import {
  PartnerCameraIcon,
  PartnerContractIcon,
  PartnerHelpIcon,
  PartnerHomeIcon,
  PartnerHoursIcon,
  PartnerListingsIcon,
  PartnerMenusIcon,
  PartnerPinIcon,
} from "@/components/partner/PartnerNavIcons";

import {
  Building2,
  MapPin,
  MapPinned,
  Image as ImageIcon,
  Images,
  LogOut,
  Plus,
  Search,
  ChevronRight,
  Loader2,
  ExternalLink,
  Menu,
  X,
  Mail,
  Phone,
  Globe,
  MousePointerClick,
  CalendarDays,
  CheckCircle2,
  Clock,
  PencilLine,
  AlertTriangle,
  Check,
  Store,
  Pencil,
  Send,
  MessageCircle,
  Headphones,
  BookOpen,
  Copy,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  listMyPartnerClients,
  getPartnerDashboard,
  getPartnerAccess,
  getOwnOnboardingToken,
} from "@/lib/partner.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/partner/")({
  head: () => ({
    meta: [
      { title: "Partner Dashboard — What Finland" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PartnerHome,
});

type View =
  | "dashboard"
  | "listings"
  | "locations"
  | "photos"
  | "menus"
  | "hours"
  | "contract"
  | "help";

const INTER = '"Inter Variable", Inter, ui-sans-serif, system-ui, sans-serif';
const NAVY = "#0b1d3a";

function PartnerHome() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fn = useServerFn(listMyPartnerClients);
  const accessFn = useServerFn(getPartnerAccess);
  const dashFn = useServerFn(getPartnerDashboard);
  const tokenFn = useServerFn(getOwnOnboardingToken);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [view, setView] = useState<View>("dashboard");
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState<string>("all"); // "all" or city_slug
  const [addingLocation, setAddingLocation] = useState(false);
  const [editor, setEditor] = useState<{ clientId: string; tab: PartnerEditorTab; newEvent?: boolean } | null>(null);

  function openEditor(clientId: string, tab: PartnerEditorTab = "profile", newEvent = false) {
    setEditor({ clientId, tab, newEvent });
  }

  async function handleAddLocation() {
    if (addingLocation) return;
    setAddingLocation(true);
    try {
      const res = await tokenFn();
      if (res.ok) {
        toast.message("Submit the onboarding form. We review it in CRM › Partner Applications, then it goes to the Publish Queue — it will not go live by itself.");
        navigate({ to: "/onboard/$token" as never, params: { token: res.token } as never });
        return;
      }
      toast.error(res.message || "Couldn't open the listing form.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setAddingLocation(false);
    }
  }

  async function handleSignOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/partner/login", replace: true });
  }

  const { data: access, isLoading: accessLoading } = useQuery({
    queryKey: ["partner", "access"],
    queryFn: () => accessFn(),
  });

  const status = access?.signup?.status;
  const needsOnboarding =
    !!access && !access.hasClient && (!access.signup || status === "draft");

  useEffect(() => {
    if (needsOnboarding) navigate({ to: "/partner/onboarding" as never, replace: true });
  }, [needsOnboarding, navigate]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["partner", "my-clients"],
    queryFn: () => fn(),
    enabled: !!access && !needsOnboarding,
  });

  const listings = data ?? [];

  // Cities available across this partner's listings (dedup by slug).
  const cityOptions = useMemo(() => {
    const map = new Map<string, string>();
    listings.forEach((l) => {
      if (l.city_slug) map.set(l.city_slug, l.city_name || l.city_slug);
    });
    return Array.from(map, ([slug, name]) => ({ slug, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [listings]);

  // If the selected city is no longer present, snap back to "all".
  useEffect(() => {
    if (cityFilter !== "all" && !cityOptions.some((c) => c.slug === cityFilter)) {
      setCityFilter("all");
    }
  }, [cityFilter, cityOptions]);

  const cityScopedListings = useMemo(() => {
    if (cityFilter === "all") return listings;
    return listings.filter((l) => l.city_slug === cityFilter);
  }, [listings, cityFilter]);

  const listingCount = cityScopedListings.length;
  const primaryClientId = cityScopedListings[0]?.client_id ?? null;

  const { data: dashRaw } = useQuery({
    queryKey: ["partner", "dashboard", primaryClientId],
    queryFn: () => dashFn({ data: { clientId: primaryClientId! } }),
    enabled: !!primaryClientId,
  });

  // Scope the places list inside the dashboard payload to the selected city.
  const dash = useMemo(() => {
    if (!dashRaw) return dashRaw;
    if (cityFilter === "all") return dashRaw;
    return {
      ...dashRaw,
      places: (dashRaw.places ?? []).filter((p) => p.city_slug === cityFilter),
    };
  }, [dashRaw, cityFilter]);

  const filteredListings = useMemo(() => {
    if (!search.trim()) return cityScopedListings;
    const q = search.trim().toLowerCase();
    return cityScopedListings.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        (l.email ?? "").toLowerCase().includes(q),
    );
  }, [cityScopedListings, search]);

  if (accessLoading || !access || needsOnboarding) {
    return (
      <div className="h-dvh w-full flex items-center justify-center bg-[#eef1f6] text-slate-500" style={{ fontFamily: INTER }}>
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const businessName =
    access?.signup?.business_name || access?.signup?.contact_name || "Partner";

  const statusBadge = ((): { label: string; className: string } => {
    if (access.hasClient || status === "approved")
      return { label: "Approved", className: "bg-emerald-100 text-emerald-700 border-emerald-200" };
    if (status === "rejected")
      return { label: "Rejected", className: "bg-rose-100 text-rose-700 border-rose-200" };
    return { label: "Pending", className: "bg-amber-100 text-amber-700 border-amber-200" };
  })();

  const initials = businessName
    .split(" ")
    .map((w: string) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const viewMeta: Record<View, { title: string; crumb: string }> = {
    dashboard: { title: "Home", crumb: "Home" },
    listings: { title: "My Listings", crumb: "My Listings" },
    locations: { title: "My Places", crumb: "My Places" },
    photos: { title: "Photos", crumb: "Photos" },
    menus: { title: "Menus", crumb: "Menus" },
    hours: { title: "Hours", crumb: "Hours" },
    contract: { title: "My Plan", crumb: "My Plan" },
    help: { title: "Get Help", crumb: "Get Help" },
  };

  function go(v: View) {
    setView(v);
    setSidebarOpen(false);
  }

  return (
    <div className="h-dvh overflow-hidden flex bg-[#eef1f6] text-slate-900 not-italic" style={{ fontFamily: INTER }}>
      {sidebarOpen ? (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <aside
        className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-[240px] shrink-0 flex flex-col text-white transition-transform duration-150`}
        style={{ background: NAVY }}
      >
        <div className="h-14 shrink-0 flex items-center justify-between px-5 border-b border-white/10">
          <span className="flex items-center gap-[0.4em] min-w-0 leading-none select-none not-italic">
            <span className="text-[15px] font-semibold tracking-tight text-white">what</span>
            <span className="text-white/35 font-semibold">·</span>
            <span className="text-[15px] font-semibold tracking-tight text-white">partner</span>
          </span>
          <button
            type="button"
            className="rounded-lg p-1 text-white/50 hover:text-white lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-4 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl grid place-items-center bg-white/10 text-white text-sm font-bold shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate not-italic">{businessName}</p>
              <p className="text-[10px] uppercase tracking-wider text-white/55 truncate">
                <span className="inline-block size-1.5 rounded-full bg-emerald-400 mr-1.5 align-middle" />
                {statusBadge.label}
              </p>
            </div>
          </div>
        </div>

        <div className="px-3 pt-4 flex-1 min-h-0 overflow-y-auto">
          <div className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35">
            Workspace
          </div>
          <nav className="space-y-1">
            <SideItem icon={<PartnerHomeIcon />} label="Home" active={view === "dashboard"} onClick={() => go("dashboard")} />
            <SideItem icon={<PartnerListingsIcon />} label="Listings" badge={listingCount} active={view === "listings"} onClick={() => go("listings")} />
            <SideItem icon={<PartnerPinIcon />} label="Locations" active={view === "locations"} onClick={() => go("locations")} />
            <SideItem icon={<PartnerCameraIcon />} label="Photos" active={view === "photos"} onClick={() => go("photos")} />
            <SideItem icon={<PartnerMenusIcon />} label="Menus" active={view === "menus"} onClick={() => go("menus")} />
            <SideItem icon={<PartnerHoursIcon />} label="Hours" active={view === "hours"} onClick={() => go("hours")} />
            <SideItem icon={<PartnerContractIcon />} label="Contract" active={view === "contract"} onClick={() => go("contract")} />
          </nav>

          <div className="mt-6 mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35">
            Support
          </div>
          <nav className="space-y-1">
            <SideItem icon={<PartnerHelpIcon />} label="Help" active={view === "help"} onClick={() => go("help")} />
          </nav>
        </div>

        <div className="p-3 mt-auto shrink-0">
          <button
            type="button"
            onClick={handleSignOut}
            className="flex w-full items-center gap-3 rounded-2xl px-2 py-[7px] text-[13.5px] font-medium text-[#ff6b7a] hover:bg-rose-500/10"
          >
            <span className="grid size-9 place-items-center rounded-[11px] bg-white/[0.06] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]">
              <LogOut className="h-4 w-4" />
            </span>
            Sign Out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        <header className="shrink-0 h-14 flex items-center gap-3 border-b border-slate-200/80 bg-white/80 backdrop-blur px-3 sm:px-5">
          <button
            type="button"
            className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="hidden sm:flex items-center gap-1.5 text-[13px] text-slate-400 min-w-0">
            <span>Partner</span>
            <span>›</span>
            <span className="font-semibold text-slate-800 truncate">{viewMeta[view].crumb}</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {cityOptions.length > 0 && (
              <div className="relative">
                <MapPin className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <select
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  aria-label="Filter by city"
                  className="h-9 appearance-none rounded-full border border-slate-200 bg-slate-50 pl-8 pr-8 text-[13px] font-medium text-slate-700 focus:border-navy/30 focus:bg-white focus:outline-none"
                >
                  <option value="all">All cities ({listings.length})</option>
                  {cityOptions.map((c) => {
                    const n = listings.filter((l) => l.city_slug === c.slug).length;
                    return (
                      <option key={c.slug} value={c.slug}>
                        {c.name} ({n})
                      </option>
                    );
                  })}
                </select>
                <ChevronRight className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 rotate-90 text-slate-400" />
              </div>
            )}
            <label className="relative hidden md:flex items-center gap-2 h-9 w-56 rounded-full bg-slate-100 px-3 text-slate-400">
              <Search className="size-3.5 shrink-0" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search listings…"
                className="bg-transparent text-[13px] text-slate-700 placeholder:text-slate-400 outline-none w-full not-italic"
              />
            </label>
            <div className="hidden sm:flex items-center gap-2 border-l border-slate-200 pl-3">
              <div className="size-8 grid place-items-center rounded-full text-white text-[11px] font-semibold" style={{ background: NAVY }}>
                {initials}
              </div>
              <div className="hidden lg:block min-w-0">
                <div className="text-[13px] font-semibold leading-tight text-slate-900 truncate max-w-[10rem]">{businessName}</div>
                <div className="text-[11px] leading-tight text-slate-500">Partner</div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-5 md:p-6 pb-24 lg:pb-6">
          <div className="max-w-6xl mx-auto space-y-4">
            {view !== "help" && view !== "contract" ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <h1 className="text-xl sm:text-[22px] font-semibold tracking-tight text-slate-900 not-italic">
                  {view === "dashboard" ? "Home" : viewMeta[view].title}
                </h1>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  {view === "dashboard" && "Your live pages and listing status"}
                  {view === "listings" && "Open a listing to edit. New listings use the onboarding form and go to Partner Applications for review."}
                  {view === "locations" && "Addresses on file. Add another venue if your plan allows it."}
                  {view === "photos" && "Gallery images across your listings."}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusBadge.className}`}>
                  {statusBadge.label}
                </span>
                {view === "locations" ? (
                  <button
                    type="button"
                    onClick={handleAddLocation}
                    disabled={addingLocation}
                    className="inline-flex items-center justify-center gap-1.5 h-9 rounded-full px-3.5 text-[13px] font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ background: NAVY }}
                  >
                    {addingLocation ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    Add location
                  </button>
                ) : view === "listings" && listingCount > 0 ? (
                  <button
                    type="button"
                    onClick={handleAddLocation}
                    disabled={addingLocation}
                    className="inline-flex items-center justify-center gap-1.5 h-9 rounded-full px-3.5 text-[13px] font-semibold text-white disabled:opacity-60"
                    style={{ background: NAVY }}
                  >
                    {addingLocation ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    Add listing
                  </button>
                ) : null}
              </div>
            </div>
            ) : null}

            {isLoading && view !== "help" && (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            )}
            {error && (
              <p className="text-rose-600 text-sm">Couldn't load your data.</p>
            )}

            {!isLoading && !error && view === "dashboard" && (
              <DashboardView
                listingCount={listingCount}
                listings={filteredListings}
                dash={dash}
                onOpenListings={() => go("listings")}
                onEdit={openEditor}
              />
            )}
            {!isLoading && !error && view === "listings" && (
              <ListingsView listings={filteredListings} onEdit={openEditor} onAddListing={handleAddLocation} addingListing={addingLocation} />
            )}
            {!isLoading && !error && view === "locations" && (
              <LocationsView dash={dash} onAddLocation={handleAddLocation} addingLocation={addingLocation} />
            )}
            {!isLoading && !error && view === "photos" && (
              <PhotosView dash={dash} listings={filteredListings} />
            )}
            {!isLoading && !error && view === "menus" && (
              <PartnerMenusView listings={filteredListings} />
            )}
            {!isLoading && !error && view === "hours" && (
              <PartnerHoursView listings={filteredListings} />
            )}
            {!isLoading && !error && view === "contract" && (
              <PartnerPlanView
                client={dash?.client ?? null}
                contract={(dash as any)?.contract ?? null}
                crmContract={(dash as any)?.crmContract ?? null}
              />
            )}
            {view === "help" && (
              <PartnerHelpView
                partnerName={businessName}
                email={filteredListings[0]?.email}
                onAddLocation={handleAddLocation}
                addingLocation={addingLocation}
                onGo={go}
              />
            )}
          </div>
        </main>
      </div>

      {editor ? (
        <DialogPrimitive.Root
          open
          onOpenChange={(open) => {
            if (!open) setEditor(null);
          }}
        >
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
            <DialogPrimitive.Content
              aria-describedby={undefined}
              className="fixed inset-0 z-[71] flex h-dvh w-full flex-col overflow-hidden rounded-none bg-[#eef1f6] shadow-2xl outline-none sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-[min(92dvh,900px)] sm:w-[calc(100vw-1.5rem)] sm:max-w-5xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl"
              onPointerDownOutside={(e) => e.preventDefault()}
              onInteractOutside={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => e.preventDefault()}
            >
              <DialogPrimitive.Title className="sr-only">Edit listing</DialogPrimitive.Title>
              <PartnerListingEditor
                key={editor.clientId + editor.tab}
                clientId={editor.clientId}
                initialTab={editor.tab}
                autoOpenEvent={!!editor.newEvent}
                variant="modal"
                onClose={() => setEditor(null)}
              />
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      ) : null}

      <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-slate-200 bg-white/95 backdrop-blur lg:hidden">
        <BottomItem icon={<PartnerHomeIcon size={20} />} label="Home" active={view === "dashboard"} onClick={() => go("dashboard")} />
        <BottomItem icon={<PartnerListingsIcon size={20} />} label="Listings" active={view === "listings"} onClick={() => go("listings")} />
        <BottomItem icon={<PartnerMenusIcon size={20} />} label="Menus" active={view === "menus"} onClick={() => go("menus")} />
        <BottomItem icon={<PartnerHoursIcon size={20} />} label="Hours" active={view === "hours"} onClick={() => go("hours")} />
        <BottomItem icon={<PartnerHelpIcon size={20} />} label="Help" active={view === "help"} onClick={() => go("help")} />
      </nav>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Views
// -----------------------------------------------------------------------------

type DashData = {
  client: { name?: string | null; company_name?: string | null; email?: string | null; phone?: string | null; address?: string | null; website?: string | null; allowed_locations?: number | null; city_name?: string | null } | null;
  onboarding: { images?: unknown; status?: string | null; submitted_at?: string | null } | null;
  places: Array<{ id: string; name: string; slug: string; clicks_month: number | null; clicks_total: number | null; last_click_at: string | null; section: string | null; city_slug: string | null; image_url?: string | null; gallery_urls?: string[] | null; published?: boolean | null }>;
  contract: Record<string, unknown> | null;
  crmContract?: Record<string, unknown> | null;
} | undefined;

function placeLivePath(p: { id: string; slug?: string | null; section?: string | null }) {
  const seg = p.section === "happening" ? "happening" : p.section === "do" ? "do" : "eat";
  return p.slug ? `/${seg}/${p.slug}` : `/place/${p.id}`;
}

function DashboardView({
  listings,
  dash,
  onOpenListings,
  onEdit,
}: {
  listingCount: number;
  listings: Listing[];
  dash: DashData;
  onOpenListings: () => void;
  onEdit: (clientId: string, tab?: PartnerEditorTab, newEvent?: boolean) => void;
}) {
  const places = dash?.places ?? [];
  const livePlaces = places.filter((p) => p.published !== false);

  async function copyLiveUrl(path: string) {
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Live URL copied");
    } catch {
      toast.error("Couldn't copy the URL");
    }
  }

  if (listings.length === 0) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm grid place-items-center text-center">
        <div>
          <div className="mx-auto mb-3 size-11 rounded-full grid place-items-center bg-slate-100 text-slate-500">
            <Store className="h-5 w-5" />
          </div>
          <h2 className="text-sm font-semibold text-slate-900 not-italic">No listings yet</h2>
          <p className="mt-1 text-[13px] text-slate-500 max-w-xs mx-auto">Create your first listing to appear on What Finland.</p>
          <Link
            to={"/partner/onboarding" as never}
            className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold text-white"
            style={{ background: NAVY }}
          >
            <Plus className="h-3.5 w-3.5" /> Start onboarding
          </Link>
        </div>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">

          <div>
            <h2 className="text-sm font-semibold text-slate-900 not-italic">Live pages</h2>
            <p className="text-[11px] text-slate-500">Public URL for each listing</p>
          </div>
          <button
            type="button"
            onClick={onOpenListings}
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-slate-600 hover:text-slate-900"
          >
            Manage <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <ul className="divide-y divide-slate-100">
          {listings.map((l, i) => {
            const place = i === 0 ? (livePlaces[0] ?? places[0]) : livePlaces.find((p) => p.name === l.name);
            const isLive = listingBucket(l.listing_status) === "live" && !!place;
            const path = place ? placeLivePath(place) : null;
            const st = STATUS_BADGE[String(l.listing_status ?? "draft")] ?? STATUS_BADGE.draft;
            return (
              <li key={l.client_id} className="flex flex-wrap items-center gap-2.5 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="truncate text-[13px] font-semibold text-slate-900 not-italic">{l.name}</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.className}`}>{st.label}</span>
                    {(l.pending_edits ?? 0) > 0 && (
                      <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                        {l.pending_edits} in review
                      </span>
                    )}
                  </div>
                  {isLive && path ? (
                    <div className="mt-0.5 truncate text-[12px] text-slate-500">{path}</div>
                  ) : (
                    <div className="mt-0.5 text-[12px] text-slate-400">Not public yet</div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {isLive && path && (
                    <>
                      <button
                        type="button"
                        onClick={() => copyLiveUrl(path)}
                        className="inline-flex h-8 items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <Copy className="h-3 w-3" /> Copy
                      </button>
                      <a
                        href={path}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-8 items-center gap-1 rounded-full bg-[#0b1d3a] px-2.5 text-[12px] font-semibold text-white hover:bg-[#132a52]"
                      >
                        <ExternalLink className="h-3 w-3" /> Open
                      </a>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => onEdit(l.client_id, "profile")}
                    className="inline-flex h-8 items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

type StatusStep = {
  key: "draft" | "review" | "live";
  label: string;
  tone: string;
  icon: React.ReactNode;
  action: string;
  actionHelp: string;
  to: string | null;
  cta: string;
};

function resolveStatus(l: Listing): StatusStep {
  const raw = String(l.listing_status ?? "draft");
  const photos = l.photos_count ?? 0;
  const pending = l.pending_edits ?? 0;

  if (raw === "rejected") {
    return {
      key: "draft",
      label: "Needs Fixes",
      tone: "bg-rose-100 text-rose-700 border-rose-200",
      icon: <AlertTriangle className="h-4 w-4" />,
      action: "Fix the details we flagged, then submit again.",
      actionHelp: "Open your listing, update the info, and press Submit For Review.",
      to: `/partner/${l.client_id}?tab=profile`,
      cta: "Fix And Resubmit",
    };
  }
  if (raw === "submitted" || raw === "in_review") {
    return {
      key: "review",
      label: "In Review",
      tone: "bg-amber-100 text-amber-700 border-amber-200",
      icon: <Clock className="h-4 w-4" />,
      action: "Nothing to do — our team is checking your listing.",
      actionHelp: "Usually 1–2 working days. We email you as soon as it is live.",
      to: `/partner/${l.client_id}?tab=profile`,
      cta: "View Submission",
    };
  }
  if (raw === "live" || raw === "approved") {
    if (pending > 0) {
      return {
        key: "live",
        label: "Live",
        tone: "bg-emerald-100 text-emerald-700 border-emerald-200",
        icon: <CheckCircle2 className="h-4 w-4" />,
        action: `${pending} change${pending === 1 ? "" : "s"} waiting for our review.`,
        actionHelp: "Your page stays live with the old info until we approve the change.",
        to: `/partner/${l.client_id}?tab=changes`,
        cta: "See My Changes",
      };
    }
    if (photos < 3) {
      return {
        key: "live",
        label: "Live",
        tone: "bg-emerald-100 text-emerald-700 border-emerald-200",
        icon: <CheckCircle2 className="h-4 w-4" />,
        action: "Add more photos — listings with 3+ pictures get more visits.",
        actionHelp: `You have ${photos} photo${photos === 1 ? "" : "s"}. You can add up to 10.`,
        to: `/partner/${l.client_id}?tab=photos`,
        cta: "Add Photos",
      };
    }
    return {
      key: "live",
      label: "Live",
      tone: "bg-emerald-100 text-emerald-700 border-emerald-200",
      icon: <CheckCircle2 className="h-4 w-4" />,
      action: "You are all set. Keep your page fresh by adding an event.",
      actionHelp: "Events show on your page and in What's Happening.",
      to: `/partner/${l.client_id}?tab=events`,
      cta: "Add An Event",
    };
  }
  return {
    key: "draft",
    label: "Draft",
    tone: "bg-slate-100 text-slate-700 border-slate-200",
    icon: <PencilLine className="h-4 w-4" />,
    action:
      photos === 0
        ? "Add at least 1 photo, then submit your listing for review."
        : "Finish your details and submit your listing for review.",
    actionHelp: "Nothing is public until our team approves it.",
    to: `/partner/${l.client_id}?tab=${photos === 0 ? "photos" : "profile"}`,
    cta: photos === 0 ? "Add Photos" : "Finish And Submit",
  };
}

function ListingStatusPanel({ listings }: { listings: Listing[] }) {
  if (listings.length === 0) return null;
  return (
    <section className="space-y-3">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
        Listing status
      </h2>
      <div className="grid gap-3">
        {listings.map((l) => {
          const s = resolveStatus(l);
          return (
            <div
              key={l.client_id}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-900 not-italic">{l.name}</div>
                  {l.city_name && (
                    <div className="mt-0.5 flex items-center gap-1 text-[12px] text-slate-500">
                      <MapPin className="h-3 w-3" /> {l.city_name}
                    </div>
                  )}
                </div>
                <span
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${s.tone}`}
                >
                  {s.icon}
                  {s.label}
                </span>
              </div>

              <div className="mt-3 flex items-center gap-2">
                {(["draft", "review", "live"] as const).map((stage, i) => {
                  const order = { draft: 0, review: 1, live: 2 } as const;
                  const reached = order[s.key] >= order[stage];
                  return (
                    <div key={stage} className="flex flex-1 items-center gap-2">
                      <div className="flex-1">
                        <div
                          className={`h-1.5 rounded-full ${reached ? "bg-[#0b1d3a]" : "bg-slate-200"}`}
                        />
                        <div
                          className={`mt-1 text-[10px] font-semibold uppercase tracking-wide ${reached ? "text-slate-800" : "text-slate-400"}`}
                        >
                          {stage === "draft" ? "Draft" : stage === "review" ? "In Review" : "Live"}
                        </div>
                      </div>
                      {i < 2 && <span className="sr-only">then</span>}
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 rounded-xl bg-slate-50 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Next step
                </div>
                <p className="mt-1 text-[13px] font-medium text-slate-900 leading-snug">{s.action}</p>
                <p className="mt-0.5 text-[12px] text-slate-500 leading-snug">{s.actionHelp}</p>
                {s.to && (
                  <Link
                    to={s.to as never}
                    className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[12px] font-semibold text-white"
                    style={{ background: NAVY }}
                  >
                    {s.cta} <ChevronRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ActionTile({
  icon,
  title,
  sub,
  tone,
  to,
  onClick,
  busy,
}: {
  icon: React.ReactNode;
  title: string;
  sub: string;
  tone: "indigo" | "emerald" | "amber" | "rose";
  to?: string;
  onClick?: () => void;
  busy?: boolean;
}) {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100",
    emerald: "bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100",
    amber: "bg-amber-50 text-amber-600 group-hover:bg-amber-100",
    rose: "bg-rose-50 text-rose-600 group-hover:bg-rose-100",
  }[tone];
  const body = (
    <>
      <span className={`size-9 grid place-items-center rounded-lg shrink-0 transition ${tones}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-slate-900 not-italic leading-tight">{title}</span>
        <span className="block text-[11px] text-slate-500 leading-tight mt-0.5">{sub}</span>
      </span>
      <ChevronRight className="size-4 shrink-0 text-slate-300 group-hover:text-slate-500" />
    </>
  );
  const cls =
    "group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-left shadow-sm transition hover:border-slate-300 hover:shadow-md disabled:opacity-60";
  if (to) {
    return (
      <Link to={to as never} className={cls}>
        {body}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={busy} className={cls}>
      {body}
    </button>
  );
}


type Listing = {
  client_id: string;
  name: string;
  email: string | null;
  city_slug?: string | null;
  city_name?: string | null;
  cover_url?: string | null;
  category?: string | null;
  section?: string | null;
  listing_status?: string | null;
  photos_count?: number;
  pending_edits?: number;
  updated_at?: string | null;
};

function listingBucket(status: string | null | undefined): "live" | "review" | "draft" {
  const raw = String(status ?? "draft");
  if (raw === "live" || raw === "approved") return "live";
  if (raw === "submitted" || raw === "in_review") return "review";
  return "draft";
}

function ListingsView({
  listings,
  onEdit,
  onAddListing,
  addingListing,
}: {
  listings: Listing[];
  onEdit: (clientId: string, tab?: PartnerEditorTab, newEvent?: boolean) => void;
  onAddListing: () => void;
  addingListing?: boolean;
}) {
  const [statusFilter, setStatusFilter] = useState<"all" | "live" | "review" | "draft">("all");
  const filtered = statusFilter === "all" ? listings : listings.filter((l) => listingBucket(l.listing_status) === statusFilter);
  const counts = {
    all: listings.length,
    live: listings.filter((l) => listingBucket(l.listing_status) === "live").length,
    review: listings.filter((l) => listingBucket(l.listing_status) === "review").length,
    draft: listings.filter((l) => listingBucket(l.listing_status) === "draft").length,
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {([
          { key: "all", label: "All" },
          { key: "live", label: "Live" },
          { key: "review", label: "In review" },
          { key: "draft", label: "Draft" },
        ] as const).map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatusFilter(f.key)}
            className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[12px] font-semibold transition ${
              statusFilter === f.key
                ? "bg-[#0b1d3a] text-white"
                : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-slate-300"
            }`}
          >
            {f.label}
            <span className={`tabular-nums ${statusFilter === f.key ? "text-white/70" : "text-slate-400"}`}>{counts[f.key]}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <section className="rounded-2xl border border-slate-200 bg-white px-5 py-12 text-center shadow-sm">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
            <Building2 className="h-5 w-5" />
          </div>
          <h3 className="text-sm font-semibold text-slate-900 not-italic">
            {listings.length === 0 ? "No listings yet" : "No listings in this filter"}
          </h3>
          <p className="mx-auto mt-1 mb-4 max-w-xs text-[13px] text-slate-500">
            {listings.length === 0
              ? "Create your first listing to showcase your business on What Finland."
              : "Try another status, or add a new location."}
          </p>
          {listings.length === 0 ? (
            <Link
              to={"/partner/onboarding" as never}
              className="inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold text-white"
              style={{ background: NAVY }}
            >
              <Plus className="h-3.5 w-3.5" /> Start onboarding
            </Link>
          ) : (
            <button
              type="button"
              onClick={onAddListing}
              disabled={addingListing}
              className="inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold text-white disabled:opacity-60"
              style={{ background: NAVY }}
            >
              {addingListing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Add listing
            </button>
          )}
        </section>
      ) : (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <ul className="divide-y divide-slate-100">
            {filtered.map((l) => {
              const st = STATUS_BADGE[String(l.listing_status ?? "draft")] ?? STATUS_BADGE.draft;
              return (
                <li key={l.client_id} className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:gap-4 sm:px-4 sm:py-3">
                  <button
                    type="button"
                    onClick={() => onEdit(l.client_id, "profile")}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <div className="relative size-[72px] shrink-0 overflow-hidden rounded-xl bg-slate-100 sm:h-[72px] sm:w-[104px]">
                      {l.cover_url ? (
                        <img src={l.cover_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                      ) : (
                        <div className="grid h-full w-full place-items-center text-slate-400">
                          <Building2 className="h-6 w-6" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-[14px] font-semibold text-slate-900 not-italic">{l.name}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.className}`}>{st.label}</span>
                        {(l.pending_edits ?? 0) > 0 && (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                            {l.pending_edits} pending
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-slate-500">
                        {l.section && <span className="capitalize">{l.section}</span>}
                        {l.category && <span>· {l.category}</span>}
                        {l.city_name && (
                          <span className="inline-flex items-center gap-1">
                            · <MapPin className="h-3 w-3" />{l.city_name}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 text-[11px] text-slate-400">
                        <span className="inline-flex items-center gap-1"><ImageIcon className="h-3 w-3" />{l.photos_count ?? 0} photos</span>
                        <span>{l.updated_at ? `Updated ${new Date(l.updated_at).toLocaleDateString()}` : "—"}</span>
                      </div>
                    </div>
                  </button>
                  <div className="flex shrink-0 flex-wrap items-center gap-1.5 sm:justify-end">
                    <button
                      type="button"
                      onClick={() => onEdit(l.client_id, "profile")}
                      className="inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[12px] font-semibold text-white"
                      style={{ background: NAVY }}
                    >
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => onEdit(l.client_id, "photos")}
                      className="inline-flex h-8 items-center gap-1.5 rounded-full bg-white px-3 text-[12px] font-semibold text-slate-700 ring-1 ring-slate-200 hover:ring-slate-300"
                    >
                      <ImageIcon className="h-3 w-3" /> Photos
                    </button>
                    <button
                      type="button"
                      onClick={() => onEdit(l.client_id, "events", true)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-full bg-white px-3 text-[12px] font-semibold text-slate-700 ring-1 ring-slate-200 hover:ring-slate-300"
                    >
                      <CalendarDays className="h-3 w-3" /> Event
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  live:       { label: "Live",        className: "bg-emerald-100 text-emerald-700" },
  approved:   { label: "Approved",    className: "bg-emerald-100 text-emerald-700" },
  submitted:  { label: "In Review",   className: "bg-amber-100 text-amber-700" },
  in_review:  { label: "In Review",   className: "bg-amber-100 text-amber-700" },
  draft:      { label: "Draft",       className: "bg-slate-100 text-slate-700" },
  rejected:   { label: "Needs Fixes", className: "bg-rose-100 text-rose-700" },
};

function ListingsCards({
  listings,
  onEdit,
}: {
  listings: Listing[];
  onEdit?: (clientId: string, tab?: PartnerEditorTab, newEvent?: boolean) => void;
}) {
  if (listings.length === 0) {
    return (
      <div className="px-5 py-12 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
          <Building2 className="h-5 w-5" />
        </div>
        <h3 className="text-sm font-semibold text-slate-900">No Listings Yet</h3>
        <p className="mx-auto mt-1 mb-4 max-w-xs text-xs text-slate-500">
          Create your first listing to showcase your business on What Finland.
        </p>
        <Link
          to={"/partner/onboarding" as never}
          className="inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold text-white"
          style={{ background: NAVY }}
        >
          <Plus className="h-3.5 w-3.5" /> Start onboarding
        </Link>
      </div>
    );
  }
  return (
    <div className="grid gap-4 p-5 sm:grid-cols-2 xl:grid-cols-3">
      {listings.map((l) => {
        const st = STATUS_BADGE[String(l.listing_status ?? "draft")] ?? STATUS_BADGE.draft;
        const body = (
          <>
          <div className="relative aspect-[16/9] w-full overflow-hidden bg-slate-100">
              {l.cover_url ? (
                <img src={l.cover_url} alt={l.name} className="h-full w-full object-cover transition group-hover:scale-105" loading="lazy" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-slate-400">
                  <Building2 className="h-8 w-8" />
                </div>
              )}
              <div className="absolute left-2 top-2 flex flex-wrap gap-1">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.className}`}>{st.label}</span>
                {(l.pending_edits ?? 0) > 0 && (
                  <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                    {l.pending_edits} Edit{(l.pending_edits ?? 0) === 1 ? "" : "s"} Pending
                  </span>
                )}
              </div>
              <div className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white">
                <ImageIcon className="mr-1 inline h-3 w-3" />
                {l.photos_count ?? 0}
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-2 p-4">
              <div>
                <div className="line-clamp-1 text-sm font-semibold text-slate-900">{l.name}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
                  {l.section && <span className="capitalize">{l.section}</span>}
                  {l.category && <span>· {l.category}</span>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                {l.city_name && (
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{l.city_name}</span>
                )}
                {l.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{l.email}</span>}
              </div>
              <div className="mt-auto flex items-center justify-between pt-2 text-xs text-slate-500">
                <span>
                  {l.updated_at ? `Updated ${new Date(l.updated_at).toLocaleDateString()}` : "—"}
                </span>
                <span className="inline-flex items-center gap-1 font-semibold text-slate-800 group-hover:text-slate-950">
                  <Pencil className="h-3 w-3" /> Edit
                </span>
              </div>
            </div>
          </>
        );
        const cls = "group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition hover:border-slate-300 hover:shadow-md text-left w-full";
        return onEdit ? (
          <button key={l.client_id} type="button" onClick={() => onEdit(l.client_id, "profile")} className={cls}>
            {body}
          </button>
        ) : (
          <Link
            key={l.client_id}
            to={"/partner/$clientId" as never}
            params={{ clientId: l.client_id } as never}
            className={cls}
          >
            {body}
          </Link>
        );
      })}
    </div>
  );
}

function LocationsView({
  dash,
  onAddLocation,
  addingLocation,
}: {
  dash: DashData;
  onAddLocation: () => void;
  addingLocation: boolean;
}) {
  const client = dash?.client;
  const places = dash?.places ?? [];

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-base font-semibold text-slate-900">Primary Contact</h2>
        <p className="text-xs text-slate-500">From your CRM record</p>
        <dl className="mt-4 space-y-3 text-sm">
          <InfoRow icon={<Building2 className="h-4 w-4" />} label="Business" value={client?.company_name || client?.name} />
          <InfoRow icon={<MapPin className="h-4 w-4" />} label="Address" value={client?.address} />
          <InfoRow icon={<Mail className="h-4 w-4" />} label="Email" value={client?.email} />
          <InfoRow icon={<Phone className="h-4 w-4" />} label="Phone" value={client?.phone} />
          <InfoRow icon={<Globe className="h-4 w-4" />} label="Website" value={client?.website} isLink />
        </dl>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Live Locations</h2>
            <p className="text-xs text-slate-500">{places.length} published</p>
          </div>
          <button
            type="button"
            onClick={onAddLocation}
            disabled={addingLocation}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
            style={{ background: NAVY }}
          >
            {addingLocation ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Add location
          </button>
        </div>
        {places.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">No published locations yet.</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {places.map((p) => (
              <li key={p.id} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                <div className="min-w-0">
                  <div className="truncate font-semibold text-slate-900">{p.name}</div>
                  <div className="truncate text-xs text-slate-500">{p.section || "—"} · {p.city_slug || "—"}</div>
                </div>
                <a
                  href={placeLivePath(p)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                >
                  View <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function PhotosView({
  dash,
  listings,
}: {
  dash: DashData;
  listings: Array<{ client_id: string; name: string; email: string | null }>;
}) {
  const raw = dash?.onboarding?.images;
  const onboardingImgs: string[] = Array.isArray(raw)
    ? (raw as unknown[])
        .map((v) => {
          if (typeof v === "string") return v;
          if (v && typeof v === "object" && "url" in v && typeof (v as { url: unknown }).url === "string") {
            return (v as { url: string }).url;
          }
          return null;
        })
        .filter((v): v is string => !!v)
    : [];

  const placeGroups = (dash?.places ?? []).map((p) => {
    const imgs: string[] = [];
    if (p.image_url) imgs.push(p.image_url);
    if (Array.isArray(p.gallery_urls)) imgs.push(...p.gallery_urls.filter((u): u is string => typeof u === "string"));
    return { id: p.id, name: p.name, images: Array.from(new Set(imgs)) };
  }).filter((g) => g.images.length > 0);

  const clientId = listings[0]?.client_id ?? null;
  const uploadHref = clientId ? (`/partner/${clientId}?tab=photos` as const) : null;

  const total = onboardingImgs.length + placeGroups.reduce((s, g) => s + g.images.length, 0);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Photo Gallery</h2>
          <p className="text-xs text-slate-500">{total} photo{total === 1 ? "" : "s"} on file</p>
        </div>
        {uploadHref ? (
          <Link
            to={uploadHref as never}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
          >
            <Plus className="h-3.5 w-3.5" /> Upload Photos
          </Link>
        ) : null}
      </div>

      {total === 0 ? (
        <div className="mt-6 rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
          No photos yet.{" "}
          {uploadHref ? (
            <Link to={uploadHref as never} className="font-semibold text-indigo-600 hover:underline">
              Click Upload Photos
            </Link>
          ) : (
            "Open a listing to upload."
          )}{" "}
          to add up to 10 images per listing (JPG, PNG, WEBP).
        </div>
      ) : (
        <div className="mt-5 space-y-6">
          {onboardingImgs.length > 0 && (
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">From Onboarding</div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {onboardingImgs.map((src, i) => (
                  <a key={`ob-${i}`} href={src} target="_blank" rel="noreferrer" className="group relative aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-100">
                    <img src={src} alt={`Photo ${i + 1}`} className="h-full w-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
                  </a>
                ))}
              </div>
            </div>
          )}
          {placeGroups.map((g) => (
            <div key={g.id}>
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{g.name}</div>
                {clientId && (
                  <Link to={`/partner/${clientId}?tab=photos` as never} className="text-xs font-semibold text-indigo-600 hover:underline">
                    Manage
                  </Link>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {g.images.map((src, i) => (
                  <a key={`${g.id}-${i}`} href={src} target="_blank" rel="noreferrer" className="group relative aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-100">
                    <img src={src} alt={`${g.name} ${i + 1}`} className="h-full w-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function InfoRow({ icon, label, value, isLink }: { icon: React.ReactNode; label: string; value?: string | number | null; isLink?: boolean }) {
  const v = value == null || value === "" ? "—" : String(value);
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-slate-400">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        {isLink && v !== "—" ? (
          <a href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer" className="break-words text-sm font-medium text-indigo-600 hover:underline">
            {v}
          </a>
        ) : (
          <div className="break-words text-sm font-medium text-slate-900">{v}</div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sidebar / card primitives
// -----------------------------------------------------------------------------

function SideItem({
  icon,
  label,
  active,
  badge,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  badge?: number;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-3 rounded-2xl px-2 py-[7px] text-[13.5px] font-medium transition-colors"
      style={{
        background: active ? "rgba(255,255,255,0.10)" : "transparent",
        color: active ? "#ffffff" : "rgba(255,255,255,0.74)",
      }}
    >
      <span
        className="grid size-9 shrink-0 place-items-center rounded-[11px] transition-all"
        style={
          active
            ? {
                background: "#ffffff",
                color: NAVY,
                boxShadow: "0 1px 0 rgba(255,255,255,0.7) inset, 0 8px 16px rgba(0,0,0,0.22)",
              }
            : {
                background: "rgba(255,255,255,0.07)",
                color: "rgba(255,255,255,0.82)",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.10)",
              }
        }
      >
        {icon}
      </span>
      <span className="flex-1 text-left tracking-[-0.01em]">{label}</span>
      {badge != null && badge > 0 && (
        <span className="min-w-[18px] h-[18px] rounded-full bg-white/15 px-1.5 text-[10px] font-bold text-white grid place-items-center">
          {badge}
        </span>
      )}
    </button>
  );
}

function StatCard({
  label,
  value,
  hint,
  icon,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  icon: React.ReactNode;
  tone: "indigo" | "emerald" | "amber" | "rose";
}) {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
  }[tone];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-3.5 py-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
            {label}
          </div>
          <div className="mt-1 text-[22px] font-semibold leading-none text-slate-900 truncate not-italic tabular-nums">{value}</div>
        </div>
        <div className={`size-8 grid place-items-center rounded-lg shrink-0 ${tones}`}>
          {icon}
        </div>
      </div>
      <div className="mt-1.5 text-[11px] text-slate-500 leading-tight">{hint}</div>
    </div>
  );
}

function BottomItem({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1 py-2 text-[10px] font-semibold transition-colors ${
        active ? "text-[#0b1d3a]" : "text-slate-500"
      }`}
    >
      <span
        className={`grid size-8 place-items-center rounded-[10px] ${
          active ? "bg-[#0b1d3a] text-white shadow-sm" : "bg-slate-100 text-slate-500"
        }`}
      >
        {icon}
      </span>
      <span>{label}</span>
    </button>
  );
}
